import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { match } from "ts-pattern";

import DockAddStepperActionComponent from "@/components/Stepper/DockAddStepperAction";
import type { IMPORT_CSV_FORM_HASH_VALUE } from "../-constants/hash";
import { IMPORT_CSV_FORM_HASH } from "../-constants/hash";
import type { MutateTableRows } from "../types";

type Props = {
  link: {
    hash: string;
    to: string;
  };
  isError: boolean;
  uploadedFileKey?: string;
  mutateTableRows: MutateTableRows;
};

const DockAddStepperAction = ({
  link,
  isError,
  uploadedFileKey,
  mutateTableRows,
}: Props) => {
  const navigate = useNavigate();

  const previousPage = useMemo(
    () =>
      match(link.hash as IMPORT_CSV_FORM_HASH_VALUE)
        .with(IMPORT_CSV_FORM_HASH.UPLOAD_FILE, () => null)
        .with(
          IMPORT_CSV_FORM_HASH.PREVIEW_FILE,
          () => IMPORT_CSV_FORM_HASH.UPLOAD_FILE,
        )
        .with(
          IMPORT_CSV_FORM_HASH.IMPORT_DATA,
          () => IMPORT_CSV_FORM_HASH.PREVIEW_FILE,
        )
        .exhaustive(),
    [link.hash],
  );

  const nextPage = useMemo(
    () =>
      match(link.hash as IMPORT_CSV_FORM_HASH_VALUE)
        .with(
          IMPORT_CSV_FORM_HASH.UPLOAD_FILE,
          () => IMPORT_CSV_FORM_HASH.PREVIEW_FILE,
        )
        .with(
          IMPORT_CSV_FORM_HASH.PREVIEW_FILE,
          () => IMPORT_CSV_FORM_HASH.IMPORT_DATA,
        )
        .with(IMPORT_CSV_FORM_HASH.IMPORT_DATA, () => null)
        .exhaustive(),
    [link.hash],
  );

  const disabled = useMemo(
    () =>
      match(link.hash as IMPORT_CSV_FORM_HASH_VALUE)
        .with(IMPORT_CSV_FORM_HASH.UPLOAD_FILE, () => !uploadedFileKey)
        .with(IMPORT_CSV_FORM_HASH.PREVIEW_FILE, () => isError)
        .with(IMPORT_CSV_FORM_HASH.IMPORT_DATA, () => isError)
        .exhaustive(),
    [link.hash, uploadedFileKey, isError],
  );

  const handleNextClick = () => {
    if (nextPage === IMPORT_CSV_FORM_HASH.PREVIEW_FILE) {
      mutateTableRows({
        dryrun: true,
      });
      navigate({ to: link.to, hash: nextPage });
      return;
    }
    if (nextPage === IMPORT_CSV_FORM_HASH.IMPORT_DATA) {
      mutateTableRows({
        dryrun: false,
      });
      navigate({ to: link.to, hash: nextPage });
      return;
    }
  };

  return (
    <DockAddStepperActionComponent
      baseLink={link.to}
      previousPage={previousPage}
      nextPage={nextPage}
      disabled={disabled}
      onNextClick={handleNextClick}
    />
  );
};

export default DockAddStepperAction;
